
/// <amd-module name="Build/Designer/YamlEditorHeader" />
import * as React from "react";

import { BuildDefinition } from "Build/Client/Contracts/Build";
import { BranchDropdown } from "Build/Common/Library/Legacy/Components/BranchDropdown";
import { EditorConstants } from "Build/Designer/Constants";
import * as Resources from "Build/Designer/Resources";
import { ensureYamlExtension, getInvalidFileNameMessage, trimFilePath, trimLeadingSlashes } from "Build/Designer/Util/File";


import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { css } from "VSS/Platform/Layout";

import { Callout } from "VSSUI/Callout";
import { FormItem } from "VSSUI/FormItem";
import { HeaderCommandBar } from "VSSUI/HeaderCommandBar";
import { Icon, IconSize } from "VSSUI/Icon";
import { TextField } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { KeyCode } from "VSSUI/Util";
import { Location } from "VSSUI/Utilities/Position";
import { VssIcon } from "VSSUI/VssIcon";

export function focusYamlAssistantButton() {
    const elementToFocus = document.querySelector("." + EditorConstants.YamlAssistantToggleElement) as HTMLElement;
    if (elementToFocus && (elementToFocus as any).focus
        && !elementToFocus.hasAttribute("disabled") && !elementToFocus.classList.contains("disabled")
        && elementToFocus.style.visibility !== "hidden" && elementToFocus.style.display !== "none") {
        elementToFocus.focus();
    }
}

/**
 * Props for the YAML editor subheader containing repository, branch, and file information
 */
export interface YamlEditorHeaderProps {
    repositoryName: string;
    repositoryIconName: string;
    repositoryUrl: string;
    branchName: string;
    fileName: string;
    fileUrl: string;
    supportFPS: boolean;
    isYamlDirty: boolean;
    definition?: BuildDefinition;

    sidePanelIsOpen: boolean | undefined;
    onSidePanelButtonClick: () => void;

    onBranchSelected: (branchName: string) => void;
    onBranchPickerError?: (errorMessage: string) => void;

    onFileNameChanged?: (fileName: string) => void;
}

interface YamlEditorHeaderState {
}

/**
 * The YAML editor subheader containing repository, branch, and file information
 */
export class YamlEditorHeader extends React.Component<YamlEditorHeaderProps, YamlEditorHeaderState> {

    public render(): JSX.Element {
        const props = this.props;
        const isPanelOpen = props.sidePanelIsOpen;
        return (
            <div className="ci-yaml-editor-header flex-row absolute-fill">
                {this.props.fileName && <>
                    {this._getBranchPicker()}
                    <div className="subheader-items flex-row flex-grow scroll-hidden">
                        {this._getRepository()}
                        <EditableFilePath
                            fileName={props.fileName}
                            fileUrl={props.fileUrl}
                            supportsFPS={props.supportFPS}
                            isYamlDirty={props.isYamlDirty}
                            onFileNameChanged={props.onFileNameChanged} />
                    </div>
                    {!isPanelOpen &&
                        <HeaderCommandBar items={[
                            {
                                id: "show-hide-tasks",
                                className: css("show-yaml-side-panel-button", EditorConstants.YamlAssistantToggleElement),
                                text: isPanelOpen ? "" : Resources.SidePanelOpen,
                                onActivate: props.onSidePanelButtonClick,
                                important: true,
                                iconProps: { iconName: isPanelOpen ? "ClosePane" : "OpenPane" },
                                subtle: true
                            }
                        ]} />
                    }
                </>}
            </div>
        );
    }

    private _getBranchPicker(): JSX.Element | undefined {
        const repository = this.props.definition && this.props.definition.repository;
        if (repository) {
            return (
                <BranchDropdown
                    className="branch-picker"
                    ariaLabel={Resources.Branch}
                    repositoryType={repository.type}
                    repositoryId={repository.id}
                    defaultBranch={this.props.branchName}
                    connectionId={repository.properties && repository.properties["connectedServiceId"]}
                    isDrodownFullWidth={false}
                    comboBoxMinWidth={120}
                    comboBoxMaxWidth={350}
                    onBranchSelected={this.props.onBranchSelected}
                    onError={this.props.onBranchPickerError} />
            );
        }
    }

    private _getRepository(): JSX.Element {
        return (<>
            <div className="subheader-item ci-hover-behavior flex-row flex-center">
                {this.props.repositoryIconName && <VssIcon iconName={this.props.repositoryIconName} />}
                <Tooltip text={this.props.repositoryName} overflowOnly={true}>
                    {this.props.repositoryUrl
                        ? <FPSLink
                            className="repository-name text-ellipsis"
                            supportsFPS={this.props.supportFPS}
                            href={this.props.repositoryUrl}>
                            {this.props.repositoryName}
                        </FPSLink>
                        : <span className="repository-name text-ellipsis">{this.props.repositoryName}</span>
                    }
                </Tooltip>
            </div>
            <div className="flex-row flex-center">
                <span className="separator">{"/"}</span>
            </div>
        </>);
    }
}

/**
 * Props for the YAML file path component. It is editable if onFileNameChanged() is provided.
 */
interface IEditableFilePathProps {
    fileName: string;
    fileUrl: string;
    supportsFPS: boolean;
    isYamlDirty: boolean;
    onFileNameChanged?: (fileName: string) => void;
}

interface IEditableFilePathState {
    isEditMode: boolean;
    fileName: string;
    errorMessage?: string;
}

/**
 * The YAML file path component. It is editable if onFileNameChanged() is provided.
 */
class EditableFilePath extends React.Component<IEditableFilePathProps, IEditableFilePathState> {
    private _textField = React.createRef<TextField>();
    private _textDiv = React.createRef<HTMLDivElement>();

    constructor(props: IEditableFilePathProps) {
        super(props);
        this.state = { isEditMode: false, fileName: props.fileName };
    }

    public render(): JSX.Element {
        return <>
            {this.state.isEditMode
                ? this._renderFilePathTextField()
                : this._renderFilePath()
            }
        </>;
    }

    private _renderFilePathTextField(): JSX.Element {
        return (
            <div ref={this._textDiv} className={"flex-grow"}>
                <FormItem error={!!this.state.errorMessage} className={"flex-grow"}>
                    <TextField
                        containerClassName={"file-path-text flex-grow"}
                        className={"editable-file-path"}
                        value={this.state.fileName}
                        autoFocus={true}
                        ref={this._textField}
                        onChange={this._onFilePathChanged}
                        onKeyDown={this._onKeyDown}
                        onBlur={this._editModeOff} />
                </FormItem>
                {this.state.errorMessage && <Callout
                    contentShadow={true}
                    anchorElement={this._textDiv.current!}
                    anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                    calloutOrigin={{ horizontal: Location.start, vertical: Location.start }}
                    anchorOffset={{ horizontal: 8, vertical: 4 }}>
                    <div className={"file-path-error"}>{this.state.errorMessage}</div>
                </Callout>
                }
            </div>
        );
    }

    private _renderFilePath(): JSX.Element {
        let path = this.props.fileName;
        path = path.replace("\\", "/");
        path = path.startsWith("./") ? path.substr(2) : path;
        path = path.startsWith("/") ? path.substr(1) : path;

        const dirtyMark = this.props.isYamlDirty ? " *" : "";
        const lastSlashIndex = path.lastIndexOf("/");
        const filePath = lastSlashIndex > 0 ? path.substr(0, lastSlashIndex) : "";
        const fileName = (lastSlashIndex > 0 ? path.substr(lastSlashIndex + 1) : path) + dirtyMark;
        const isEditable = !!this.props.onFileNameChanged;

        return (
            <Tooltip text={isEditable ? Resources.Rename : undefined} >
                <div
                    className={css("editable-path-container flex-center flex-row text-ellipsis", isEditable && "editable")}
                    {...(isEditable ? { tabIndex: 0, onClick: this._editModeOn, onFocus: this._editModeOn } : {})}>
                    {filePath && <>
                        <div className="file-path flex-row flex-center">
                            <Tooltip text={path} overflowOnly={true}>
                                <span className="text-ellipsis white-space-pre">{filePath}</span>
                            </Tooltip>
                        </div>
                        <div className="flex-row flex-center">{"/"}</div>
                    </>}
                    <div className="subheader-item ci-hover-behavior flex-row flex-center">
                        <Tooltip text={path} overflowOnly={true}>
                            <>
                                {this.props.fileUrl
                                    ? <FPSLink
                                        className="file-name text-ellipsis"
                                        supportsFPS={this.props.supportsFPS}
                                        href={this.props.fileUrl}>
                                        {fileName}
                                    </FPSLink>
                                    : <span className="file-name text-ellipsis">{fileName}</span>
                                }
                                {isEditable && <Icon className="rename-icon" iconName="Rename" size={IconSize.medium} />}
                            </>
                        </Tooltip>
                    </div>
                </div>
            </Tooltip>
        );
    }

    private _editModeOn = () => {
        this.setState({ isEditMode: true }, () => {
            this._textField.current && this._textField.current.select();
        });
    };

    private _editModeOff = (discard?: boolean, callback?: () => void) => {
        const errorMessage = discard ? undefined : this.state.errorMessage;
        const fileName = discard ? this.props.fileName : this._cleanupFileName(this.state.fileName);
        if (!errorMessage) {
            this.setState({ fileName, errorMessage, isEditMode: false }, () => {
                if (this.props.onFileNameChanged && fileName !== this.props.fileName) {
                    this.props.onFileNameChanged(fileName);
                }
                callback && callback();
            });
        }
    };

    private _onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (ev.which === KeyCode.enter || ev.which === KeyCode.escape) {
            ev.preventDefault();
            ev.stopPropagation();
            const discard = ev.which === KeyCode.escape;
            this._editModeOff(discard, focusYamlAssistantButton);
        }
    };

    private _onFilePathChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
        const errorMessage = getInvalidFileNameMessage(value);
        this.setState({ fileName: value, errorMessage })
    };

    private _cleanupFileName(name: string): string {
        name = trimFilePath(name);
        name = trimLeadingSlashes(name);
        return ensureYamlExtension(name);
    }
}
